import React, { useEffect, useState, Fragment } from "react";

import { ReactSVG } from "react-svg";
import { Menu, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";

import NewToolTip from "../common/New-ToolTip";
import RootModal from "./RootModal";
import CustomInput from "./CustomInput";
import { formatUTCDate, capitalizeFirstLetter, formatWordsIntoLines } from "../../helpers/functions";
import {
  categoryOptions,
  severityOptions,
  typeOptions,
} from "../patient/clinical-services/service-steps/clinical-assessment/data";
import ConfirmationModalNew from "./ConfirmationModalNew";
import {
  updateAllergy,
  fetchAllergy,
} from "../../redux/modules/patient/actions";
import { AllergyValueLabelOptions } from "../patient/create/options";
import AllergyCreate from "../patient/clinical-details/components/allergy/AllergyCreate";
import RootModalWithAction from "./RootModalWithAction";
import {
  updateClinicalService,
  updateMedicationReview,
  fetchClinicalServices,
} from "../../redux/modules/clinical-services/actions";
import { useParams } from "react-router-dom";
import { ClinicalServiceTypes } from "../patient/text/data";
import { useClickOutsideTooltip } from "../../hooks/useClickOutsideTooltip";

const ListPopup = ({ title, open, handleClose, allergies = [], patientId }) => {
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);

  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { clinicalServiceObj, clinicalServiceName, clinicalServicesSmall } =
    useSelector((state) => state.clinicalService);

  // Add/Edit
  const [create, setCreate] = useState(true);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [showFooterActions, setShowFooterActions] = useState(false);
  const [localAllergies, setLocalAllergies] = useState(
    allergies?.map((x) => ({ ...x })) || []
  );
  const [tableFilter, setTableFilter] = useState("all");
  // snapshot of active allergies before opening the form
  const allergySnapshotRef = React.useRef("");
  const allergyIdSnapshotRef = React.useRef([]);
  const justClosedDrawerRef = React.useRef(false);

  // for delete (store allergy id, not list index)
  const [allergyIdToDelete, setAllergyIdToDelete] = useState(null);
  const [openDeleteAllergy, setOpenDeleteAllergy] = useState(false);
  // for restore (store allergy id)
  const [allergyIdToRestore, setAllergyIdToRestore] = useState(null);
  const [openRestoreAllergy, setOpenRestoreAllergy] = useState(false);

  // Get all in-progress clinical services (including current active service)
  const inProgressServices = React.useMemo(() => {
    const services = [];

    // Add in-progress minor ailments from clinicalServicesSmall
    if (clinicalServicesSmall?.minorAilments) {
      clinicalServicesSmall.minorAilments
        .filter((service) => service.status === "inProgress")
        .forEach((service) => {
          services.push({
            id: service.id,
            type: "minorAilment",
            serviceType: service.minorAilmentTypeCode,
            confirmedAllergies:
              service.initialAssessment?.initialAssessmentForm
                ?.clinicalAssessment?.confirmedAllergy ?? [],
          });
        });
    }

    // Add in-progress medication reviews from clinicalServicesSmall
    if (clinicalServicesSmall?.medicationReviews) {
      clinicalServicesSmall.medicationReviews
        .filter((service) => service.status === "inProgress")
        .forEach((service) => {
          services.push({
            id: service.id,
            type: "medicationReview",
            serviceType: service.medicationReviewTypeCode,
            confirmedAllergies:
              service.initialAssessment?.initialAssessmentForm
                ?.clinicalAssessment?.confirmedAllergy ?? [],
          });
        });
    }

    // Add current active service if exists
    if (serviceId && clinicalServiceName) {
      const currentServiceExists = services.some(
        (service) => service.id === serviceId
      );

      if (!currentServiceExists) {
        // Add current active service to the list
        const serviceType =
          clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
            ? "minorAilment"
            : "medicationReview";
        services.push({
          id: serviceId,
          type: serviceType,
          serviceType: clinicalServiceName,
          confirmedAllergies:
            clinicalServiceObj?.initialAssessment?.initialAssessmentForm
              ?.clinicalAssessment?.confirmedAllergy ?? [],
        });
      }
    }

    return services;
  }, [
    clinicalServicesSmall,
    serviceId,
    clinicalServiceName,
    clinicalServiceObj,
  ]);

  const [confirmedAllergies, setConfirmedAllergies] = useState(
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
      ?.clinicalAssessment?.confirmedAllergy ?? []
  );

  // Sync local list when allergies change (including edits)
  const prevAllergyKeyRef = React.useRef("");
  useEffect(() => {
    const sorted = [...(allergies || [])].sort((a, b) =>
      String(a.id).localeCompare(String(b.id))
    );
    // Use JSON.stringify to detect any changes in allergy data (including edits)
    const key = JSON.stringify(sorted);
    if (key !== prevAllergyKeyRef.current) {
      setLocalAllergies(sorted.map((x) => ({ ...x })));
      prevAllergyKeyRef.current = key;
    }
  }, [allergies]);

  // Track drawer state to detect when it closes
  const prevDrawerStateRef = React.useRef(showCreateSlider);

  // Check for changes when drawer closes or allergies update
  useEffect(() => {
    const wasOpen = prevDrawerStateRef.current;
    const justClosed = wasOpen && !showCreateSlider;
    
    if (justClosed && allergySnapshotRef.current) {
      justClosedDrawerRef.current = true;
    }

    if (allergySnapshotRef.current && !showCreateSlider) {
      const getActiveAllergies = (list) => 
        (list || []).filter((a) => a.status !== "inactive").sort((a, b) => String(a.id) > String(b.id) ? 1 : -1);

      const currentActive = getActiveAllergies(allergies).length >= getActiveAllergies(localAllergies).length
        ? getActiveAllergies(allergies)
        : getActiveAllergies(localAllergies);

      const snapshotCount = allergyIdSnapshotRef.current?.length || 0;
      const currentIds = currentActive.map((a) => a.id).sort((a, b) => String(a) > String(b) ? 1 : -1);
      const snapshotIds = allergyIdSnapshotRef.current || [];

      const hasChanged = 
        currentActive.length !== snapshotCount ||
        JSON.stringify(currentIds) !== JSON.stringify(snapshotIds) ||
        JSON.stringify(currentActive) !== allergySnapshotRef.current;

      if (hasChanged) {
        setShowFooterActions(true);
        justClosedDrawerRef.current = false;
      }
    }

    prevDrawerStateRef.current = showCreateSlider;
  }, [showCreateSlider, allergies, localAllergies]);

  // Reset state when popup closes
  useEffect(() => {
    if (!open) {
      // Reset all state when popup closes
      setShowFooterActions(false);
      setShowCreateSlider(false);
      setSelectedAllergy(null);
      setCreate(true);
      setOpenTooltipId(null);
      setOpenMenuIndex(null);
      allergySnapshotRef.current = "";
      allergyIdSnapshotRef.current = [];
      justClosedDrawerRef.current = false;
    }
  }, [open]);

  // Fetch clinical services when needed
  useEffect(() => {
    if (patientId && !clinicalServicesSmall) {
      dispatch(fetchClinicalServices(patientId));
    }
  }, [patientId, clinicalServicesSmall, dispatch]);

  const lastUpdatedDate = allergies
    ?.map((allergy) => allergy.confirmedDate)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  // Helper function to take snapshot of current allergies
  const takeSnapshot = () => {
    const active = (allergies || [])
      .filter((a) => a.status !== "inactive")
      .sort((a, b) => String(a.id) > String(b.id) ? 1 : -1);
    allergySnapshotRef.current = JSON.stringify(active);
    allergyIdSnapshotRef.current = active.map((a) => a.id).sort((a, b) => String(a) > String(b) ? 1 : -1);
  };

  const manageClick = () => {
    document.body.classList.toggle("overflow-hidden");
  };

  const filterOptions = React.useMemo(
    () => [
      { value: "all", label: "All" },
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    []
  );

  const filteredAllergies = React.useMemo(() => {
    if (tableFilter === "active") {
      return localAllergies.filter((allergy) => allergy.status === "active");
    }
    if (tableFilter === "inactive") {
      return localAllergies.filter((allergy) => allergy.status === "inactive");
    }
    return localAllergies;
  }, [localAllergies, tableFilter]);

  const [sortedAllergies, setSortedAllergies] = useState([]);

  useEffect(() => {
    const list = [...filteredAllergies].sort((a, b) => {
      const nameA = a.allergyName?.toLowerCase() || "";
      const nameB = b.allergyName?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });
    setSortedAllergies(list);
  }, [filteredAllergies]);

  // Enable scroll when there are many records (for performance) or a single/last record
  const shouldShowScrollbar = sortedAllergies.length > 6 || sortedAllergies.length === 1;

  const deleteAllergy = (id) => {
    if (!id) return;
    const allergyToUpdate = localAllergies.find((a) => a.id === id);
    if (!allergyToUpdate) return;

    const updatedAllergy = {
      ...allergyToUpdate,
      status: "inactive",
      previousStatus: allergyToUpdate.status,
    };
    const { id: allergyId, ...updatedObject } = updatedAllergy;
    dispatch(updateAllergy(allergyId, updatedObject, false, 'deleted'));

    // Update the same list that drives rendering (by id, not by index)
    setLocalAllergies((prev) =>
      prev.map((allergy) => (allergy.id === allergyId ? updatedAllergy : allergy))
    );
    // Reset confirmation state when allergies are modified
    setConfirmedAllergies([]);
    setShowFooterActions(true);
  };

  const restoreAllergy = (id) => {
    if (!id) return;
    const allergyToUpdate = localAllergies.find((a) => a.id === id);
    if (!allergyToUpdate) return;

    const updatedAllergy = {
      ...allergyToUpdate,
      status: "active",
      confirmedDate: null,
      previousStatus: allergyToUpdate.status,
    };
    const { id: allergyId, ...updatedObject } = updatedAllergy;
    dispatch(updateAllergy(allergyId, updatedObject, false, 'restored'));

    setLocalAllergies((prev) =>
      prev.map((allergy) => (allergy.id === allergyId ? updatedAllergy : allergy))
    );
    setConfirmedAllergies([]);
    setShowFooterActions(true);
  };

  // Centralized function to update clinical services with confirmed allergies
  const updateClinicalServicesWithAllergies = (confirmedAllergyIds) => {
    // Always update ALL in-progress services for consistency
    if (inProgressServices.length > 0) {
      inProgressServices.forEach((service) => {
        const updatedSteps = {
          clinicalAssessment: {
            allergy: confirmedAllergyIds.length > 0,
            confirmedAllergy: confirmedAllergyIds,
          },
        };

        if (service.type === "minorAilment") {
          dispatch(updateClinicalService(service.id, updatedSteps));
        } else if (service.type === "medicationReview") {
          dispatch(updateMedicationReview(service.id, updatedSteps));
        }
      });
    }
  };

  // Centralized function to update allergy records
  const updateAllergyRecords = (allergiesToUpdate) => {
    allergiesToUpdate.forEach((item) => {
      const completeAllergyObject = {
        ...item,
        confirmedDate: new Date(),
      };
      const { id, ...updatedObject } = completeAllergyObject;
      dispatch(updateAllergy(id, updatedObject, false, false));
    });
  };

  // Clinical service confirmation logic
  const confirmAllAllergies = () => {
    const activeAllergies = localAllergies.filter(
      (allergy) => allergy.status === "active"
    );

    // Update allergy records in backend
    updateAllergyRecords(activeAllergies);

    // Update local state
    setLocalAllergies(
      localAllergies.map((allergy) => ({
        ...allergy,
        confirmedDate:
          allergy.status === "active" ? new Date() : allergy.confirmedDate,
        confirmed: allergy.status === "active" ? true : allergy.confirmed,
      }))
    );

    // Update confirmed allergies array
    const newConfirmedAllergies = activeAllergies.map((val) => val.id);
    setConfirmedAllergies(newConfirmedAllergies);

    // Update clinical services
    updateClinicalServicesWithAllergies(newConfirmedAllergies);
  };

  return (
    <>
      <RootModalWithAction
        open={open}
        handleClose={handleClose}
        className="!min-w-[1360px] m-4"
        title={title}
        titleClassName="text-3xl text-teal-300"
        subTitle=""
        subTitleClassName={`text-sm-body text-teal-600 `}
        headClassName="px-5 py-3"
        showAdd={false}
        hideBorder={true}
        headerLayout="compact"
        hideCloseIcon={showFooterActions}
        closeOnOutsideClick={false}
        renderCompactActions={({ CloseButton }) => (
          <div className="flex items-center gap-3">
            <CustomInput
              options={filterOptions}
              name="allergy-filter"
              className="font-semibold bg-white border border-gray-100 text-body-md !w-[220px] !h-[45px] rounded-full"
              type="dropdown"
              showOptionalLabel={false}
              value={tableFilter}
              onChange={(option) => setTableFilter(option.value)}
            />
            <button
              className="disabled:opacity-40 min-w-[220px] h-[45px] rounded-full bg-REELTeal border-REELTeal hover:bg-teal-200 text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-5"
              onClick={() => {
                takeSnapshot();
                setShowFooterActions(false);
                setShowCreateSlider(true);
              }}
            >
              Add Allergy
            </button>
            {CloseButton}
          </div>
        )}
      >
        <div className="relative flex flex-col items-start gap-0 px-5 w-[1360px] flex-shrink-0 overflow-hidden">
          <div
            className={`w-full flex-1 ${
              shouldShowScrollbar ? "overflow-y-auto scrollbar-hide max-h-[290px]" : "overflow-y-visible"
            } ${!showFooterActions ? "mb-5" : ""} `}
          >
            <table className="w-full border border-gray-20">
            <thead className="bg-gray-20">
              <tr className="text-body-md h-[50px] px-6 py-3 flex items-center gap-2 self-stretch">
                <th
                  scope="col"
                  className="font-semibold text-left text-teal-300 w-[25%]"
                >
                  Allergy
                </th>
                <th
                  scope="col"
                  className="font-semibold text-left text-teal-300 w-[12%]"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="font-semibold text-left text-teal-300 w-[12%]"
                >
                  Category
                </th>
                <th
                  scope="col"
                  className="font-semibold text-left text-teal-300 w-[17%]"
                >
                  Reaction
                </th>
                <th
                  scope="col"
                  className="font-semibold text-left text-teal-300 w-[12%]"
                >
                  Severity
                </th>
                <th
                  scope="col"
                  className="font-semibold text-center text-teal-300 w-[12%]"
                >
                  Comments
                </th>
                <th
                  scope="col"
                  className="font-semibold text-center text-teal-300 w-[10%]"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 text-body-md">
              {sortedAllergies?.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-4 font-medium text-gray-900"
                  >
                    No Allergy Found
                  </td>
                </tr>
              ) : (
                sortedAllergies.map((item, i) => {
                  const severityOption = severityOptions.find(
                    (type) => type.value === item.severity
                  );
                  const severityLabel =
                    severityOption?.label || item.severity || "";
                  const isSevere =
                    severityLabel?.toLowerCase() === "severe";
                  const isStripedRow = i % 2 === 1;
                  const rowBgClass = item.status == "inactive" ? "bg-red-50" : isStripedRow ? "bg-gray-10" : "bg-white";

                  return (
                    <tr
                      className={`transition-colors duration-300 cursor-pointer group hover:bg-blue-100 h-[40px] px-6 py-4 flex items-center gap-3 self-stretch ${rowBgClass}`}
                      key={i}
                    >
                      <td
                        className={`font-medium capitalize w-[25%] ${
                          isSevere ? "text-red-500" : "text-teal-300"
                        }`}
                      >
                        {capitalizeFirstLetter(item.allergyName)}
                      </td>
                      <td className="text-gray-700 capitalize w-[12%]">
                        {typeOptions.find((type) => type.value === item.type)
                          ?.label || ""}
                      </td>
                      <td className="text-gray-700 capitalize w-[12%]">
                        {categoryOptions.find(
                          (type) => type.value === item.category
                        )?.label || ""} 
                      </td>
                      <td className="text-gray-700 w-[17%]">
                        <div
                          className="truncate max-w-[240px]"
                          data-tooltip-id={`allergy-reaction-tooltip-${item.id ?? i}`}
                        >
                          {capitalizeFirstLetter(item.reaction)}
                        </div>
                        {item.reaction && String(item.reaction).trim().length > 0 && (
                          <NewToolTip
                            id={`allergy-reaction-tooltip-${item.id ?? i}`}
                            render={() => (
                              <div className="bg-white p-2 text-teal-300 text-body-sm max-w-[460px]">
                                {capitalizeFirstLetter(item.reaction)}
                              </div>
                            )}
                          />
                        )}
                      </td>
                      <td
                        className={`capitalize w-[12%] ${
                          isSevere ? "text-red-500" : "text-gray-700"
                        }`}
                      >
                        {capitalizeFirstLetter(severityLabel)}
                      </td>
                      <td className="flex items-center justify-center w-[12%]">
                        {item.comments && (
                          <div className="flex justify-center w-full items-center pt-2">
                            <div 
                              ref={openTooltipId === `allergy_tooltip_${item.id ?? i}` ? tooltipWrapperRef : null}
                              className="relative inline-flex cursor-pointer"
                              data-tooltip-id={openTooltipId === `allergy_tooltip_${item.id ?? i}` ? undefined : `allergy_hover_tooltip_${item.id ?? i}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const tooltipId = `allergy_tooltip_${item.id ?? i}`;
                                setOpenTooltipId(openTooltipId === tooltipId ? null : tooltipId);
                              }}
                            >
                              <ReactSVG
                                src="/icons/paper-clip-new.svg"
                                className="w-6"
                              />
                              {openTooltipId !== `allergy_tooltip_${item.id ?? i}` && (
                                <NewToolTip
                                  id={`allergy_hover_tooltip_${item.id ?? i}`}
                                  render={() => (
                                    <div className="bg-white p-2 text-teal-300 text-body-sm">
                                      Click to view comments
                                    </div>
                                  )}
                                />
                              )}
                              <NewToolTip
                                id={`allergy_tooltip_${item.id ?? i}`}
                                isOpen={openTooltipId === `allergy_tooltip_${item.id ?? i}`}
                                render={() => (
                                  <div className="bg-white max-w-[460px]">
                                    <div className="flex flex-col gap-3 p-2">
                                      <div className="text-teal-300 text-sm-body prose-a:underline">
                                        <div className="flex flex-col gap-4">
                                          <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                            {formatWordsIntoLines(item.comments)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                clickable={true}
                                handleHide={() => setOpenTooltipId(null)}
                                anchorSelect={`[data-tooltip-id="allergy_hover_tooltip_${item.id ?? i}"]`}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-4 text-gray-800 whitespace-nowrap w-[10%]">
                        {item.status !== "inactive" && (
                          <div className="flex justify-center">
                            <Menu
                              as="div"
                              className="relative inline-block text-left"
                            >
                              {({ open }) => {
                                // Update state when menu opens/closes
                                if (open && openMenuIndex !== i) {
                                  setOpenMenuIndex(i);
                                } else if (!open && openMenuIndex === i) {
                                  setOpenMenuIndex(null);
                                }
                                
                                // Hide icon if it's directly below an open menu
                                const shouldHideIcon = openMenuIndex === i - 1;
                                
                                return (
                                  <>
                                    <div>
                                      <Menu.Button>
                                        <ReactSVG
                                          src={`/icons/triple-dot.svg`}
                                          className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4] ${shouldHideIcon ? 'opacity-0 pointer-events-none' : ''}`}
                                        />
                                      </Menu.Button>
                                    </div>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                                      enterTo="transform translate-y-0 opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                                    >
                                      <Menu.Items
                                        className={
                                          i === sortedAllergies.length - 1 || sortedAllergies.length === 1
                                            ? "absolute left-[-6px] min-w-[70px] w-auto origin-bottom bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bottom-full mb-0"
                                            : "absolute left-[-6px] min-w-[70px] w-auto origin-top bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-full mt-0"
                                        }
                                      >
                                        <Menu.Item>
                                          <button
                                            className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                            onClick={() => {
                                              // Don't take snapshot for edit - footer actions only for adding new allergies
                                              setShowFooterActions(false);
                                              setSelectedAllergy(item);
                                              setCreate(false);
                                              setShowCreateSlider(true);
                                            }}
                                          >
                                            Edit
                                          </button>
                                        </Menu.Item>
                                        <Menu.Item>
                                          <button
                                            className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center gap-2 hover:bg-gray-50"
                                            onClick={() => {
                                              setSelectedAllergy(item);
                                              setOpenDeleteAllergy(true);
                                              setAllergyIdToDelete(item.id);
                                            }}
                                          >
                                            Delete
                                          </button>
                                        </Menu.Item>
                                      </Menu.Items>
                                    </Transition>
                                  </>
                                );
                              }}
                            </Menu>
                          </div>
                        )}
                        {item.status === "inactive" && (
                          <div className="flex justify-end pr-12">
                            <ReactSVG
                              src="/icons/undo.svg"
                              className="w-6"
                              onClick={() => {
                                setSelectedAllergy(item);
                                setAllergyIdToRestore(item.id);
                                setOpenRestoreAllergy(true);
                              }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
      
        </div>
        {showFooterActions ? (
          <div className="flex justify-end gap-3 px-6 pb-5">
            <button
              className="inline-flex min-w-[220px] disabled:opacity-50 rounded-[24px] border transition-all duration-300 text-body-md items-center font-semibold justify-center px-4 py-2 bg-REELTeal border-REELTeal text-white hover:bg-teal-200"
              onClick={async () => {
                await confirmAllAllergies();
                setShowFooterActions(false);
                allergySnapshotRef.current = "";
                allergyIdSnapshotRef.current = [];
                handleClose();
              }}
            >
              Confirm Allergy List
            </button>
          </div>
        ) : (
          // Spacer to provide visual breathing room when no footer actions are shown
          <div className="px-6 pb-4" />
        )}
      </RootModalWithAction>
      <ConfirmationModalNew
        type="primary"
        title="Delete Allergy"
        content={() =>
          `Are you sure you want to delete ${capitalizeFirstLetter(
            selectedAllergy?.allergyName ?? ""
          )}?`
        }
        confirmAction={() => {
          deleteAllergy(allergyIdToDelete);
          setOpenDeleteAllergy(false);
          setAllergyIdToDelete(null);
          setSelectedAllergy(null);
        }}
        optionLabel="Reason"
        open={openDeleteAllergy}
        handleClose={() => {
          setOpenDeleteAllergy(false);
          setAllergyIdToDelete(null);
          setSelectedAllergy(null);
        }}
        onCross={() => {
          setOpenDeleteAllergy(false);
          setAllergyIdToDelete(null);
          setSelectedAllergy(null);
        }}
        options={AllergyValueLabelOptions}
      />

      <ConfirmationModalNew
        type="primary"
        title="Restore Allergy"
        content={() =>
          `Are you sure you want to restore ${capitalizeFirstLetter(
            selectedAllergy?.allergyName ?? ""
          )}?`
        }
        confirmAction={() => {
          restoreAllergy(allergyIdToRestore);
          setOpenRestoreAllergy(false);
          setAllergyIdToRestore(null);
          setSelectedAllergy(null);
        }}
        open={openRestoreAllergy}
        handleClose={() => {
          setOpenRestoreAllergy(false);
          setAllergyIdToRestore(null);
          setSelectedAllergy(null);
        }}
        onCross={() => {
          setOpenRestoreAllergy(false);
          setAllergyIdToRestore(null);
          setSelectedAllergy(null);
        }}
      />

      <AllergyCreate
        open={showCreateSlider}
        onClose={() => {
          setShowCreateSlider(false);
          manageClick();
          setSelectedAllergy(null);
          setCreate(true);
          setConfirmedAllergies([]);
        }}
        selectedAllergy={selectedAllergy}
        patientId={patientId}
        create={create}
        onSave={async () => {
          if (patientId) {
            await dispatch(fetchAllergy(patientId));
          }
          setShowFooterActions(true);
        }}
      />
    </>
  );
};

export default ListPopup;
